/**
 * SessionMap - Real-time session visualization using Mapbox GL JS with H3 hexagons
 *
 * Displays active sessions on a Mapbox map with:
 * - Cyan H3 hexagons for browser agent locations
 * - Purple H3 hexagons for worker agent locations
 * - 3D raised arcs (deck.gl ArcLayer) connecting browser → worker locations
 * - Optional amber "session" coordinator hex + stitching arcs (showSessions prop)
 *
 * ============================================================================
 * BUNDLE SIZE NOTE — consider React.lazy in the future
 * ============================================================================
 * This module statically imports `mapbox-gl`, `@deck.gl/mapbox`, and
 * `@deck.gl/layers` which together add ~600 KB minified (~140 KB gzipped) to
 * the console's main chunk. That's acceptable for now because the map is on
 * the primary analytics landing page, but if the main bundle becomes a
 * concern:
 *
 *   1. At each call site, replace the static import with
 *        const SessionMap = React.lazy(() => import('../../components/analytics/SessionMap').then(m => ({ default: m.SessionMap })));
 *      and wrap `<SessionMap ... />` in `<Suspense fallback={...}>`.
 *   2. Remove the re-export from `components/analytics/index.ts` (or make it
 *      a type-only export) so the barrel file doesn't pull the heavy module
 *      into every consumer of the barrel.
 *   3. Leave everything else alone — deck.gl and mapbox-gl will tree-shake
 *      into their own chunk on their own once this file is lazy.
 *
 * Call sites to update if you do this:
 *   - apps/console/src/routes/analytics/index.tsx
 *   - apps/console/src/routes/admin/reports.tsx
 * ============================================================================
 */

import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import MapGL, { Source, Layer, Popup, useControl, type MapRef, type LayerProps, type MapLayerMouseEvent } from 'react-map-gl/mapbox';
import { MapboxOverlay, type MapboxOverlayProps } from '@deck.gl/mapbox';
import { ArcLayer } from '@deck.gl/layers';
import { cellToBoundary, isValidCell, cellToLatLng, getResolution, cellToCenterChild } from 'h3-js';
import { logger } from '../../log';
import styles from './SessionMap.module.css';
import 'mapbox-gl/dist/mapbox-gl.css';

const log = logger('session-map');

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string;

export interface SessionMapProps {
  connections?: Array<{
    sessionId: string;
    browserColo: string | null;
    browserH3: string | null;
    workerColo: string | null;
    workerH3: string | null;
    sessionColo: string | null;
    sessionH3: string | null;
    /** Per-session value of the active groupBy column. Null when groupBy is unset. */
    group?: string | null;
  }>;
  isLoading?: boolean;
  /**
   * Rendering mode.
   * - `'full'` (default): browser hex + worker hex + primary browser→worker arc.
   * - `'browser-only'`: just the browser hex with a larger framing outline. Used
   *   on the org analytics page where only approximate user geolocation is shown.
   */
  mode?: 'full' | 'browser-only';
  /**
   * When true (full mode only), also render the SessionDO coordinator hex and
   * "stitching" arcs that connect browser→session and worker→session. These
   * are drawn thinner, lower, and more transparent so they read as secondary
   * routing detail layered beneath the primary browser↔worker arc.
   */
  showSessions?: boolean;
  /** Active groupBy value (e.g. "project", "app"). When set, browser hexes are colored per-group. */
  groupBy?: string;
  /**
   * Map of group value → color (hex or CSS var). The keys must match `connection.group`.
   * Built by the parent route from the same palette/order it uses for the chart legend.
   * When omitted (or `groupBy` is unset), browser hexes use the default cyan.
   */
  groupColors?: Record<string, string>;
  /**
   * Map of group value → human label for tooltips (e.g. project id → project name).
   * Optional; if omitted, the raw group value is used.
   */
  groupLabels?: Record<string, string>;
}

// Empty GeoJSON for initial state
const EMPTY_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

// Convert H3 cell to GeoJSON polygon coordinates
function h3ToPolygonCoords(h3Index: string): number[][] | null {
  try {
    if (!isValidCell(h3Index)) return null;
    const boundary = cellToBoundary(h3Index, true); // true = format as [lng, lat]
    // Close the polygon by repeating the first point
    return [...boundary, boundary[0]];
  } catch {
    return null;
  }
}

// Convert connections to GeoJSON for browser agent H3 hexagons.
//
// When `groupBy` is active, each cell tracks per-group session counts; the
// dominant group (the one with the most sessions in that cell) is stored on
// the feature so a Mapbox `match` expression can color it accordingly. The
// full breakdown is also serialized for the hover tooltip.
//
// Browser hexes are rendered as the *finer-resolution center child* of the
// underlying H3 cell. This is an intentional visual disambiguation aid: if
// browser+worker happen to land in the same H3 cell, the smaller browser hex
// remains visible inside the worker hex instead of being completely obscured.
function toBrowserH3GeoJSON(connections: SessionMapProps['connections']): GeoJSON.FeatureCollection {
  if (!connections || connections.length === 0) {
    return EMPTY_GEOJSON;
  }

  type CellData = {
    count: number;
    colos: string[];
    groupCounts: Record<string, number>;
  };
  const h3Counts: Record<string, CellData> = {};
  for (const conn of connections) {
    const h3 = conn.browserH3;
    if (!h3) continue;
    if (!h3Counts[h3]) {
      h3Counts[h3] = { count: 0, colos: [], groupCounts: {} };
    }
    const cell = h3Counts[h3];
    cell.count++;
    if (conn.browserColo && !cell.colos.includes(conn.browserColo)) {
      cell.colos.push(conn.browserColo);
    }
    if (conn.group) {
      cell.groupCounts[conn.group] = (cell.groupCounts[conn.group] ?? 0) + 1;
    }
  }

  const features: GeoJSON.Feature[] = [];
  for (const h3 of Object.keys(h3Counts)) {
    const cell = h3Counts[h3];
    const finerH3 = cellToCenterChild(h3, getResolution(h3) + 1);
    const coords = h3ToPolygonCoords(finerH3);
    if (!coords) continue;

    // Pick the dominant group (most sessions in this cell). Ties broken by
    // alphabetical group key for stability across re-renders.
    let dominantGroup: string | null = null;
    let dominantCount = 0;
    for (const [group, count] of Object.entries(cell.groupCounts)) {
      if (count > dominantCount || (count === dominantCount && (dominantGroup === null || group < dominantGroup))) {
        dominantGroup = group;
        dominantCount = count;
      }
    }

    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [coords] },
      properties: {
        h3: finerH3,
        count: cell.count,
        colos: cell.colos.join(', '),
        type: 'browser',
        dominantGroup,
        // Mapbox properties must be primitives — serialize the breakdown for tooltips.
        groupBreakdown: JSON.stringify(cell.groupCounts),
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

// Convert connections to GeoJSON for the *framing* browser hex — same cell
// as toBrowserH3GeoJSON but rendered at the original H3 resolution (parent)
// rather than the finer-resolution center child. In browser-only mode this
// is drawn as a lower-opacity halo around the inner hex so each cluster
// visually pops without a neighbouring worker hex for contrast.
function toBrowserH3FrameGeoJSON(connections: SessionMapProps['connections']): GeoJSON.FeatureCollection {
  if (!connections || connections.length === 0) {
    return EMPTY_GEOJSON;
  }

  type FrameCell = {
    count: number;
    groupCounts: Record<string, number>;
  };
  const h3Counts: Record<string, FrameCell> = {};
  for (const conn of connections) {
    const h3 = conn.browserH3;
    if (!h3) continue;
    if (!h3Counts[h3]) {
      h3Counts[h3] = { count: 0, groupCounts: {} };
    }
    h3Counts[h3].count++;
    if (conn.group) {
      h3Counts[h3].groupCounts[conn.group] = (h3Counts[h3].groupCounts[conn.group] ?? 0) + 1;
    }
  }

  const features: GeoJSON.Feature[] = [];
  for (const h3 of Object.keys(h3Counts)) {
    const cell = h3Counts[h3];
    const coords = h3ToPolygonCoords(h3);
    if (!coords) continue;

    let dominantGroup: string | null = null;
    let dominantCount = 0;
    for (const [group, count] of Object.entries(cell.groupCounts)) {
      if (count > dominantCount || (count === dominantCount && (dominantGroup === null || group < dominantGroup))) {
        dominantGroup = group;
        dominantCount = count;
      }
    }

    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [coords] },
      properties: {
        h3,
        count: cell.count,
        type: 'browser-frame',
        dominantGroup,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

// Convert connections to GeoJSON for worker H3 hexagons
function toWorkerH3GeoJSON(connections: SessionMapProps['connections']): GeoJSON.FeatureCollection {
  if (!connections || connections.length === 0) {
    return EMPTY_GEOJSON;
  }

  const h3Counts: Record<string, { count: number; colos: string[] }> = {};
  for (const conn of connections) {
    const h3 = conn.workerH3;
    if (h3) {
      if (!h3Counts[h3]) {
        h3Counts[h3] = { count: 0, colos: [] };
      }
      h3Counts[h3].count++;
      if (conn.workerColo && !h3Counts[h3].colos.includes(conn.workerColo)) {
        h3Counts[h3].colos.push(conn.workerColo);
      }
    }
  }

  const features: GeoJSON.Feature[] = [];
  for (const h3 of Object.keys(h3Counts)) {
    const coords = h3ToPolygonCoords(h3);
    if (coords) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [coords] },
        properties: {
          h3,
          count: h3Counts[h3].count,
          colos: h3Counts[h3].colos.join(', '),
          type: 'worker'
        },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

// Convert connections to GeoJSON for session H3 hexagons (where the SessionDO
// is resident). Rendered outline-only in full mode so it visually frames the
// colo without obscuring browser or worker hexes that might overlap it.
function toSessionH3GeoJSON(connections: SessionMapProps['connections']): GeoJSON.FeatureCollection {
  if (!connections || connections.length === 0) {
    return EMPTY_GEOJSON;
  }

  const h3Counts: Record<string, { count: number; colos: string[] }> = {};
  for (const conn of connections) {
    const h3 = conn.sessionH3;
    if (h3) {
      if (!h3Counts[h3]) {
        h3Counts[h3] = { count: 0, colos: [] };
      }
      h3Counts[h3].count++;
      if (conn.sessionColo && !h3Counts[h3].colos.includes(conn.sessionColo)) {
        h3Counts[h3].colos.push(conn.sessionColo);
      }
    }
  }

  const features: GeoJSON.Feature[] = [];
  for (const h3 of Object.keys(h3Counts)) {
    const coords = h3ToPolygonCoords(h3);
    if (coords) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [coords] },
        properties: {
          h3,
          count: h3Counts[h3].count,
          colos: h3Counts[h3].colos.join(', '),
          type: 'session'
        },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

// Data consumed by deck.gl's ArcLayer. `primary` is the browser↔worker logical
// connection (cyan→purple gradient, drawn at full weight). `stitch` is the
// physical routing detail via the SessionDO coordinator, split into two
// segments (browser→session, worker→session) drawn thinner and more
// transparent. Stitching arcs are only emitted when `showSessions` is true.
type ArcDatum = {
  source: [number, number];
  target: [number, number];
  sourceColor: [number, number, number, number];
  targetColor: [number, number, number, number];
  kind: 'browser-worker' | 'browser-session' | 'worker-session';
  /**
   * Tilt angle (degrees) for deck.gl's ArcLayer. Rotates the arc plane around
   * the source→target axis so overlapping arcs fan out like highway lanes
   * instead of stacking onto a single visible stroke. Assigned per-bucket at
   * build time: a single arc stays at 0, two arcs split to ±TILT_STEP, three
   * arcs become -2·TILT_STEP / 0 / +2·TILT_STEP, etc., clamped so the fan
   * never exceeds TILT_MAX.
   */
  tilt: number;
};

// Visual spacing for overlapping arcs. Tilts of ±18° read clearly as
// "separate flights" without making short arcs look distorted.
const TILT_MAX = 30;
const TILT_STEP = 12;

const COLOR_BROWSER: [number, number, number, number] = [0, 212, 255, 200];  // cyan #00d4ff
const COLOR_WORKER:  [number, number, number, number] = [168, 85, 247, 200]; // purple #a855f7
const COLOR_SESSION: [number, number, number, number] = [245, 158, 11, 200]; // amber #f59e0b

// Stitching arcs reuse the same hues at ~55% alpha so they read as secondary
// connective tissue beneath the primary browser→worker arc.
const COLOR_BROWSER_STITCH: [number, number, number, number] = [0, 212, 255, 140];
const COLOR_WORKER_STITCH:  [number, number, number, number] = [168, 85, 247, 140];
const COLOR_SESSION_STITCH: [number, number, number, number] = [245, 158, 11, 140];

/**
 * Resolve a palette color string to a concrete value both Mapbox and deck.gl
 * can consume. The palettes used by the analytics and reports routes contain
 * a mix of hex strings and `var(--name)` CSS custom properties, neither of
 * which Mapbox accepts inside a data-driven `match` expression — Mapbox
 * throws "Could not parse color from value 'var(--...)'" at layer load time.
 *
 * Returns a `#RRGGBB` string (or the fallback if the input can't be parsed).
 * Any rgb/rgba result from CSS custom property resolution is converted back
 * to hex so Mapbox's expression parser always sees something literal.
 */
function resolveColorToHex(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  let raw = value.trim();

  // Resolve CSS custom properties against the document root so themed tokens
  // (e.g. `var(--color-accent)`) work. We explicitly do NOT pass `var(...)`
  // through to Mapbox — its expression parser rejects them even when the
  // style itself would accept them as a static string.
  const varMatch = raw.match(/^var\((--[^),]+)(?:,\s*([^)]+))?\)$/);
  if (varMatch && typeof document !== 'undefined') {
    const resolved = getComputedStyle(document.documentElement).getPropertyValue(varMatch[1]).trim();
    if (resolved) {
      raw = resolved;
    } else if (varMatch[2]) {
      // `var(--x, #fallback)` — use the inline fallback.
      raw = varMatch[2].trim();
    } else {
      return fallback;
    }
  }

  // Already hex → normalize to #RRGGBB.
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return '#' + raw[1] + raw[1] + raw[2] + raw[2] + raw[3] + raw[3];
  }
  if (/^#[0-9a-f]{6}$/i.test(raw)) {
    return raw.toLowerCase();
  }

  // CSS rgb()/rgba() → hex. computed styles frequently come back this way.
  const rgbMatch = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const toHex = (n: string) => Math.max(0, Math.min(255, parseInt(n, 10))).toString(16).padStart(2, '0');
    return '#' + toHex(rgbMatch[1]) + toHex(rgbMatch[2]) + toHex(rgbMatch[3]);
  }

  return fallback;
}

/**
 * Parse a palette color string into an RGBA tuple usable by deck.gl. Builds
 * on `resolveColorToHex` so CSS custom properties and rgb() strings work.
 */
function parseColor(
  value: string | undefined,
  alpha: number,
  fallback: [number, number, number, number],
): [number, number, number, number] {
  const hex = resolveColorToHex(value, '');
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return fallback;
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
    alpha,
  ];
}

/**
 * Assign a tilt in degrees for an arc at position `index` within an
 * overlap bucket of size `total`. A single arc stays flat (0°); multiple
 * arcs fan out symmetrically around 0 at TILT_STEP increments, clamped so
 * the outermost arcs never exceed ±TILT_MAX regardless of bucket size.
 *
 * Examples:
 *   total=1 → [0]
 *   total=2 → [-12, +12]
 *   total=3 → [-12, 0, +12]
 *   total=4 → [-18, -6, +6, +18]
 *   total=5 → [-24, -12, 0, +12, +24]
 *   total=10 → clamped to [-30..+30] linearly
 */
function tiltForBucketIndex(index: number, total: number): number {
  if (total <= 1) return 0;
  // Ideal step: arcs evenly spaced, centered on 0.
  const span = (total - 1) * TILT_STEP;
  if (span <= TILT_MAX * 2) {
    return -span / 2 + index * TILT_STEP;
  }
  // Too many arcs to fit within ±TILT_MAX at the preferred step — linearly
  // distribute across the clamped range instead.
  return -TILT_MAX + (index / (total - 1)) * (TILT_MAX * 2);
}

/**
 * Group arcs that share the same endpoints and assign each one a tilt so
 * they fan out visually instead of stacking. Operates in-place on the
 * input array and returns it for chaining.
 *
 * Endpoint keys use the literal source/target values because arc endpoints
 * come from H3 cell centers via `cellToLatLng` — two sessions in the same
 * browser/worker H3 cell produce bit-identical endpoints, so no rounding
 * is needed. Stability across re-renders comes from preserving insertion
 * order within each bucket.
 */
function assignArcTilts(arcs: ArcDatum[]): ArcDatum[] {
  const buckets = new Map<string, ArcDatum[]>();
  for (const arc of arcs) {
    const key = `${arc.source[0]},${arc.source[1]}>${arc.target[0]},${arc.target[1]}`;
    const list = buckets.get(key);
    if (list) {
      list.push(arc);
    } else {
      buckets.set(key, [arc]);
    }
  }
  for (const list of buckets.values()) {
    if (list.length === 1) {
      list[0].tilt = 0;
      continue;
    }
    list.forEach((arc, i) => {
      arc.tilt = tiltForBucketIndex(i, list.length);
    });
  }
  return arcs;
}

function buildArcData(
  connections: SessionMapProps['connections'],
  showSessions: boolean,
  groupColors: Record<string, string> | undefined,
): { primary: ArcDatum[]; stitch: ArcDatum[] } {
  const primary: ArcDatum[] = [];
  const stitch: ArcDatum[] = [];
  if (!connections || connections.length === 0) return { primary, stitch };

  const centerOf = (h3: string | null | undefined): [number, number] | null => {
    if (!h3 || !isValidCell(h3)) return null;
    const [lat, lng] = cellToLatLng(h3);
    return [lng, lat];
  };
  const samePoint = (a: [number, number], b: [number, number]) =>
    a[0] === b[0] && a[1] === b[1];

  // When groupBy is active, the browser end of each arc adopts the palette
  // color for that session's group — mirroring how the browser hex recolors
  // via the Mapbox `match` expression. Worker and session ends stay on
  // their semantic colors (purple / amber) so the connection story is still
  // readable at a glance: "this group's user ↔ their worker".
  const browserColorFor = (conn: NonNullable<SessionMapProps['connections']>[number]): [number, number, number, number] => {
    if (!groupColors || !conn.group) return COLOR_BROWSER;
    return parseColor(groupColors[conn.group], 200, COLOR_BROWSER);
  };
  const browserStitchColorFor = (conn: NonNullable<SessionMapProps['connections']>[number]): [number, number, number, number] => {
    if (!groupColors || !conn.group) return COLOR_BROWSER_STITCH;
    return parseColor(groupColors[conn.group], 140, COLOR_BROWSER_STITCH);
  };

  for (const conn of connections) {
    try {
      const browser = centerOf(conn.browserH3);
      const worker = centerOf(conn.workerH3);
      const session = centerOf(conn.sessionH3);

      // Primary logical connection: browser → worker.
      // If either endpoint is missing, skip the whole session — we don't
      // fall back to partial arcs because they'd misrepresent the shape
      // of the relationship.
      if (browser && worker && !samePoint(browser, worker)) {
        primary.push({
          source: browser,
          target: worker,
          sourceColor: browserColorFor(conn),
          targetColor: COLOR_WORKER,
          kind: 'browser-worker',
          tilt: 0, // assigned below by assignArcTilts
        });
      }

      // Stitching: physical routing detail via the SessionDO coordinator.
      // Two independent segments so each can fade into the amber session
      // endpoint cleanly. Only emitted when the user asks to see sessions.
      if (showSessions && session) {
        if (browser && !samePoint(browser, session)) {
          stitch.push({
            source: browser,
            target: session,
            sourceColor: browserStitchColorFor(conn),
            targetColor: COLOR_SESSION_STITCH,
            kind: 'browser-session',
            tilt: 0,
          });
        }
        if (worker && !samePoint(worker, session)) {
          stitch.push({
            source: worker,
            target: session,
            sourceColor: COLOR_WORKER_STITCH,
            targetColor: COLOR_SESSION_STITCH,
            kind: 'worker-session',
            tilt: 0,
          });
        }
      }
    } catch { /* skip */ }
  }

  // Second pass: fan out overlapping arcs so multiple sessions between the
  // same browser/worker cells render as visibly distinct strokes instead of
  // stacking onto a single line.
  assignArcTilts(primary);
  assignArcTilts(stitch);

  return { primary, stitch };
}

// Bridge between deck.gl and react-map-gl. Registers a MapboxOverlay as a
// map control so deck.gl layers composite in sync with the Mapbox viewport.
//
// NOTE: uses overlay mode (not `interleaved: true`) because interleaved mode
// depends on Mapbox custom layers, which have known incompatibilities with
// deck.gl's ArcLayer. Requires the Mapbox map to use mercator projection
// (not globe) so deck.gl's viewport matches Mapbox's.
function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

// Layer styles - Browser H3 framing hex (outer halo, browser-only mode)
const browserH3FrameFillLayer: LayerProps = {
  id: 'browser-h3-frame-fill',
  type: 'fill',
  source: 'browser-h3-frame',
  paint: {
    'fill-color': '#00d4ff',
    'fill-opacity': ['interpolate', ['linear'], ['get', 'count'], 1, 0.08, 10, 0.14, 50, 0.2],
  },
};

const browserH3FrameOutlineLayer: LayerProps = {
  id: 'browser-h3-frame-outline',
  type: 'line',
  source: 'browser-h3-frame',
  paint: {
    'line-color': '#00d4ff',
    'line-width': 1,
    'line-opacity': 0.4,
  },
};

// Layer styles - Browser H3 hexagons (cyan)
const browserH3FillLayer: LayerProps = {
  id: 'browser-h3-fill',
  type: 'fill',
  source: 'browser-h3',
  paint: {
    'fill-color': '#00d4ff',
    'fill-opacity': ['interpolate', ['linear'], ['get', 'count'], 1, 0.3, 10, 0.5, 50, 0.7],
  },
};

const browserH3OutlineLayer: LayerProps = {
  id: 'browser-h3-outline',
  type: 'line',
  source: 'browser-h3',
  paint: {
    'line-color': '#00d4ff',
    'line-width': 1.5,
    'line-opacity': 0.8,
  },
};

// Layer styles - Worker H3 hexagons (purple)
const workerH3FillLayer: LayerProps = {
  id: 'worker-h3-fill',
  type: 'fill',
  source: 'worker-h3',
  paint: {
    'fill-color': '#a855f7',
    'fill-opacity': ['interpolate', ['linear'], ['get', 'count'], 1, 0.3, 10, 0.5, 50, 0.7],
  },
};

const workerH3OutlineLayer: LayerProps = {
  id: 'worker-h3-outline',
  type: 'line',
  source: 'worker-h3',
  paint: {
    'line-color': '#a855f7',
    'line-width': 1.5,
    'line-opacity': 0.8,
  },
};

// Layer styles - Session H3 hexagons (amber) — the SessionDO's resident colo.
// Rendered as a thin dashed outline with a very subtle fill so it reads as a
// coordinator marker without obscuring browser/worker hexes it may overlap.
const sessionH3FillLayer: LayerProps = {
  id: 'session-h3-fill',
  type: 'fill',
  source: 'session-h3',
  paint: {
    'fill-color': '#f59e0b',
    'fill-opacity': ['interpolate', ['linear'], ['get', 'count'], 1, 0.05, 10, 0.1, 50, 0.15],
  },
};

const sessionH3OutlineLayer: LayerProps = {
  id: 'session-h3-outline',
  type: 'line',
  source: 'session-h3',
  paint: {
    'line-color': '#f59e0b',
    'line-width': 1.5,
    'line-opacity': 0.8,
    'line-dasharray': [2, 2],
  },
};

export function SessionMap({ connections = [], isLoading, mode = 'full', showSessions = false, groupBy, groupColors, groupLabels }: SessionMapProps) {
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [mapError, setMapError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState<{
    longitude: number;
    latitude: number;
    breakdown: Record<string, number>;
    count: number;
  } | null>(null);

  // Build a Mapbox `match` expression that maps `dominantGroup` -> palette color.
  // When groupBy is unset OR groupColors is empty, fall back to the default cyan
  // so behavior is identical to the pre-grouping rendering.
  //
  // Mapbox's expression parser rejects `var(--name)` inside a data-driven
  // expression ("Could not parse color from value 'var(--text-primary)'"), so
  // every palette value is resolved to a concrete `#RRGGBB` at build time
  // via resolveColorToHex. If the user switches themes, this useMemo re-runs
  // because `groupColors` is rebuilt upstream on every render of the parent.
  const browserFillColor = useMemo(() => {
    const entries = groupBy && groupColors ? Object.entries(groupColors) : [];
    if (entries.length === 0) return '#00d4ff';
    // ['match', ['get', 'dominantGroup'], 'group1', '#color1', 'group2', '#color2', ..., '#fallback']
    const expr: any[] = ['match', ['get', 'dominantGroup']];
    for (const [groupValue, color] of entries) {
      expr.push(groupValue, resolveColorToHex(color, '#00d4ff'));
    }
    expr.push('#00d4ff');
    return expr;
  }, [groupBy, groupColors]);

  const browserOutlineColor = browserFillColor;

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Toggle fullscreen mode
  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      log.error('fullscreen error', err);
    }
  }, []);

  // Detect theme changes
  useEffect(() => {
    const update = () => {
      const dataTheme = document.documentElement.getAttribute('data-theme');
      setTheme(dataTheme?.includes('light') ? 'light' : 'dark');
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  // Memoize GeoJSON data with error handling
  const browserH3Data = useMemo(() => {
    try {
      return toBrowserH3GeoJSON(connections);
    } catch (e) {
      log.error('error creating browser H3 GeoJSON', e);
      return EMPTY_GEOJSON;
    }
  }, [connections]);

  const browserH3FrameData = useMemo(() => {
    if (mode !== 'browser-only') return EMPTY_GEOJSON;
    try {
      return toBrowserH3FrameGeoJSON(connections);
    } catch (e) {
      log.error('error creating browser H3 frame GeoJSON', e);
      return EMPTY_GEOJSON;
    }
  }, [connections, mode]);

  const workerH3Data = useMemo(() => {
    try {
      return toWorkerH3GeoJSON(connections);
    } catch (e) {
      log.error('error creating worker H3 GeoJSON', e);
      return EMPTY_GEOJSON;
    }
  }, [connections]);

  const sessionH3Data = useMemo(() => {
    if (mode !== 'full') return EMPTY_GEOJSON;
    try {
      return toSessionH3GeoJSON(connections);
    } catch (e) {
      log.error('error creating session H3 GeoJSON', e);
      return EMPTY_GEOJSON;
    }
  }, [connections, mode]);

  // deck.gl ArcLayer data. `primary` = browser↔worker, `stitch` = optional
  // session-routing detail. Only built in full mode — browser-only mode has
  // nothing to connect. Depends on `groupColors` so arc browser-ends recolor
  // live when the user switches groupBy.
  const arcs = useMemo<{ primary: ArcDatum[]; stitch: ArcDatum[] }>(() => {
    if (mode !== 'full') return { primary: [], stitch: [] };
    try {
      return buildArcData(connections, showSessions, groupColors);
    } catch (e) {
      log.error('error building arc data', e);
      return { primary: [], stitch: [] };
    }
  }, [connections, mode, showSessions, groupColors]);

  // Two ArcLayers so primary and stitching can have independent styling.
  // Stitching is pushed first so the primary arc renders on top in 2D; in 3D
  // the primary's higher peak also keeps it visually in front of the stitching.
  const deckLayers = useMemo(() => {
    if (mode !== 'full') return [];
    const layers: ArcLayer<ArcDatum>[] = [];
    if (arcs.stitch.length > 0) {
      layers.push(
        new ArcLayer<ArcDatum>({
          id: 'session-arcs-stitch',
          data: arcs.stitch,
          getSourcePosition: (d) => d.source,
          getTargetPosition: (d) => d.target,
          getSourceColor: (d) => d.sourceColor,
          getTargetColor: (d) => d.targetColor,
          getWidth: 1.5,
          getHeight: 0.25,
          getTilt: (d) => d.tilt,
          // NOTE: `greatCircle: true` and `getTilt` are mutually exclusive in
          // ArcLayer — when greatCircle is on, the arc is locked to a geodesic
          // on the sphere and per-arc tilt is ignored. We use the default
          // bezier arc instead so overlapping sessions can visibly fan out.
          // The visual difference from a true great circle is negligible at
          // continental scale on a Mercator projection.
          widthUnits: 'pixels',
          pickable: false,
        }),
      );
    }
    if (arcs.primary.length > 0) {
      layers.push(
        new ArcLayer<ArcDatum>({
          id: 'session-arcs-primary',
          data: arcs.primary,
          getSourcePosition: (d) => d.source,
          getTargetPosition: (d) => d.target,
          getSourceColor: (d) => d.sourceColor,
          getTargetColor: (d) => d.targetColor,
          getWidth: 1.75,
          getHeight: 0.5,
          getTilt: (d) => d.tilt,
          // NOTE: `greatCircle: true` and `getTilt` are mutually exclusive in
          // ArcLayer — when greatCircle is on, the arc is locked to a geodesic
          // on the sphere and per-arc tilt is ignored. We use the default
          // bezier arc instead so overlapping sessions can visibly fan out.
          // The visual difference from a true great circle is negligible at
          // continental scale on a Mercator projection.
          widthUnits: 'pixels',
          pickable: false,
        }),
      );
    }
    return layers;
  }, [mode, arcs]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.fallback}>
          <div className={styles.fallbackIcon}>🗺️</div>
          <div className={styles.fallbackText}>Map unavailable</div>
          <div className={styles.fallbackHint}>VITE_MAPBOX_ACCESS_TOKEN not configured</div>
        </div>
      </div>
    );
  }

  if (mapError) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.fallback}>
          <div className={styles.fallbackIcon}>⚠️</div>
          <div className={styles.fallbackText}>Map error</div>
          <div className={styles.fallbackHint}>{mapError}</div>
        </div>
      </div>
    );
  }

  const mapStyle = theme === 'light'
    ? 'mapbox://styles/mapbox/light-v11'
    : 'mapbox://styles/mapbox/dark-v11';

  return (
    <div
      ref={containerRef}
      className={`${styles.mapContainer} ${isLoading ? styles.loading : ''} ${isFullscreen ? styles.fullscreen : ''}`}
    >
      <MapGL
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: 0,
          latitude: 25,
          zoom: 1.3,
          pitch: 0,
        }}
        projection="mercator"
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        styleDiffing={false}
        collectResourceTiming={false}
        trackResize={true}
        attributionControl={false}
        interactiveLayerIds={groupBy ? ['browser-h3-fill'] : []}
        onMouseMove={(e: MapLayerMouseEvent) => {
          if (!groupBy) return;
          const feature = e.features?.[0];
          if (!feature) {
            setHoveredFeature(null);
            return;
          }
          const breakdownStr = (feature.properties?.groupBreakdown as string | undefined) ?? '{}';
          let breakdown: Record<string, number> = {};
          try { breakdown = JSON.parse(breakdownStr); } catch { /* ignore */ }
          const count = (feature.properties?.count as number | undefined) ?? 0;
          setHoveredFeature({
            longitude: e.lngLat.lng,
            latitude: e.lngLat.lat,
            breakdown,
            count,
          });
        }}
        onMouseLeave={() => setHoveredFeature(null)}
        onError={(e) => {
          // Paint-property parse errors (e.g. data-driven expressions) are
          // recoverable — the layer just falls back to the default — so
          // don't replace the whole map with an error screen. Only hard
          // failures (style load, missing sources, GL context loss) trip
          // the fallback UI.
          const message = e.error?.message || 'Unknown map error';
          const recoverable = /paint|layout|could not parse color/i.test(message);
          if (recoverable) {
            log.warn('map warning (non-fatal)', message);
          } else {
            log.error('map error', e);
            setMapError(message);
          }
        }}
      >
        {/* 3D raised arcs (browser→session / worker→session) — full mode only.
            Rendered via deck.gl's ArcLayer composited into the Mapbox canvas. */}
        {mode === 'full' && <DeckGLOverlay layers={deckLayers} />}

        {/* Browser framing hex (outer halo) — browser-only mode, for visual pop */}
        {mode === 'browser-only' && (
          <Source id="browser-h3-frame" type="geojson" data={browserH3FrameData}>
            <Layer
              {...browserH3FrameFillLayer}
              paint={{
                ...browserH3FrameFillLayer.paint,
                'fill-color': browserFillColor as any,
              }}
            />
            <Layer
              {...browserH3FrameOutlineLayer}
              paint={{
                ...browserH3FrameOutlineLayer.paint,
                'line-color': browserOutlineColor as any,
              }}
            />
          </Source>
        )}

        {/* Browser agent H3 hexagons — colored by dominant group when groupBy is set, cyan otherwise. */}
        <Source id="browser-h3" type="geojson" data={browserH3Data}>
          <Layer
            {...browserH3FillLayer}
            paint={{
              ...browserH3FillLayer.paint,
              'fill-color': browserFillColor as any,
            }}
          />
          <Layer
            {...browserH3OutlineLayer}
            paint={{
              ...browserH3OutlineLayer.paint,
              'line-color': browserOutlineColor as any,
            }}
          />
        </Source>

        {/* Worker agent H3 hexagons (purple) — full mode only */}
        {mode === 'full' && (
          <Source id="worker-h3" type="geojson" data={workerH3Data}>
            <Layer {...workerH3FillLayer} />
            <Layer {...workerH3OutlineLayer} />
          </Source>
        )}

        {/* SessionDO resident colo H3 hexagons (amber, dashed) — full mode + opt-in */}
        {mode === 'full' && showSessions && (
          <Source id="session-h3" type="geojson" data={sessionH3Data}>
            <Layer {...sessionH3FillLayer} />
            <Layer {...sessionH3OutlineLayer} />
          </Source>
        )}

        {/* Per-group breakdown popup on browser hex hover */}
        {hoveredFeature && groupBy && (
          <Popup
            longitude={hoveredFeature.longitude}
            latitude={hoveredFeature.latitude}
            anchor="bottom"
            closeButton={false}
            closeOnClick={false}
            offset={12}
          >
            <div style={{ fontSize: '12px', minWidth: '140px' }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                {hoveredFeature.count} session{hoveredFeature.count === 1 ? '' : 's'}
              </div>
              {Object.entries(hoveredFeature.breakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([group, n]) => (
                  <div key={group} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <span style={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      background: groupColors?.[group] ?? '#00d4ff',
                      marginRight: '6px',
                    }} />
                    <span style={{ flex: 1 }}>{groupLabels?.[group] ?? group}</span>
                    <span>{n}</span>
                  </div>
                ))}
            </div>
          </Popup>
        )}
      </MapGL>

      {/* Fullscreen toggle button */}
      <button
        onClick={toggleFullscreen}
        className={styles.fullscreenButton}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
          </svg>
        )}
      </button>

      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.legendHex} style={{ background: '#00d4ff' }} />
          <span>{mode === 'browser-only' ? 'User' : 'Browser'}</span>
        </div>
        {mode === 'full' && (
          <>
            <div className={styles.legendItem}>
              <span className={styles.legendHex} style={{ background: '#a855f7' }} />
              <span>Worker</span>
            </div>
            {showSessions && (
              <div className={styles.legendItem}>
                <span className={styles.legendHex} style={{ background: '#f59e0b' }} />
                <span>Session</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SessionMap;
